// @ts-check
/**
 * Adapters for the project-level data both Business View pages show: the project
 * picker in the header, and the summary strip beneath it.
 *
 * Separate from the overview and detail mappers because these two read the projects
 * list rather than either API response, and both pages render them through the same
 * shared components. Keeping them here is what lets the overview and detail folders
 * stay independent of each other.
 *
 * @module businessViewProjectMapper
 */

/** @typedef {import('./businessView.types').ProjectOption} ProjectOption */
/** @typedef {import('./businessView.types').ProjectSummary} ProjectSummary */

/**
 * Build the project picker options from the projects the user has access to.
 * Values are strings because the Material Tailwind Select compares option
 * values by identity.
 *
 * @param {any[]} [projects] Projects from ProjectsListContext (filteredFlatProjects).
 * @returns {ProjectOption[]}
 */
export const buildProjectOptions = (projects = []) =>
  projects
    .filter((project) => project?.projectId != null)
    .filter((project) => Number(project?.hasBusinessViewData) === 1)
    .map((project) => ({
      id: String(project.projectId),
      label: project.short_name || project.title || `Project ${project.projectId}`,
    }));

/**
 * Build the project summary strip from the selected project.
 *
 * @param {any} [project] A project from ProjectsListContext.
 *
 * @returns {ProjectSummary}
 */
export const mapSummary = (project) => {
	const tags = Object.values(project?.project_tags || {}).flat();
	// Deduped: tag groups can carry the same title, and the strip keys its badges by value.
	const tagValues = Array.from(new Set(tags.map((tag) => tag?.title).filter(Boolean)));

	return {
		name: project?.title || project?.short_name || '',
		description: project?.intro_text || '',
		avatarUrl: project?.logo_location || '',
		tags: tagValues,
	};
};
